import {
  AccordionItemProps,
  AccordionRootProps,
  useAccordionItemContext,
} from '@chakra-ui/react';
import { IOfferingSummaryResSchema } from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import React, { FC } from 'react';
import { useGetListData } from '../../../../api/queryUtils';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
} from '../../../../components/Monetize';
import useQuotePrices from '../../../../hooks/useQuotePrices';
import { arrayToObject } from '../../../../utils/misc';
import PricesSectionContent from './reviewQuote/PricesSectionContent';

interface QuoteFormPricesSectionProps extends AccordionRootProps {
  itemProps?: Omit<AccordionItemProps, 'value'>;
  quoteId: string;
  currency: string;
}

interface QuoteFormPricesSectionBodyProps {
  offeringsById: Record<string, IOfferingSummaryResSchema>;
  currency: string;
  quotePrices: ReturnType<typeof useQuotePrices>['quotePrices'];
  loading: boolean;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const QuoteFormPricesSectionBody = ({
  offeringsById,
  currency,
  quotePrices,
  loading,
}: QuoteFormPricesSectionBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton isExpanded={isExpanded} label="Prices" />
      <MAccordionPanel>
        <PricesSectionContent
          offeringsById={offeringsById}
          currency={currency}
          quotePrices={quotePrices}
          loading={loading}
        />
      </MAccordionPanel>
    </>
  );
};

const QuoteFormPricesSection: FC<QuoteFormPricesSectionProps> =
  React.forwardRef<any, QuoteFormPricesSectionProps>(
    (
      {
        itemProps = {},
        quoteId,
        currency,
        ...rest
      }: QuoteFormPricesSectionProps,
      ref,
    ) => {
      const { quotePrices, loading } = useQuotePrices(quoteId);
      const { data: offeringsById = {} } = useGetListData<
        IOfferingSummaryResSchema,
        Record<string, IOfferingSummaryResSchema>
      >(
        'productCatalogOfferings',
        {
          config: DEFAULT_PAGER,
          filters: {
            id: { in: quotePrices.map(({ offering }) => offering.id) },
          },
        },
        {
          enabled: quotePrices.length > 0,
          gcTime: 1000 * 60 * 10, // 10 minutes
          staleTime: 1000 * 60 * 5, // 5 minutes
          select: ({ content }) => {
            return arrayToObject(content, 'id');
          },
        },
      );

      const isEmptyData =
        !quotePrices ||
        quotePrices.length === 0 ||
        quotePrices.every(({ schedule }) => schedule.length === 0);

      if (!loading && isEmptyData) {
        return null;
      }

      return (
        <MAccordion collapsible defaultValue={['0']} {...rest}>
          <MAccordionItem {...itemProps} value="0">
            <QuoteFormPricesSectionBody
              offeringsById={offeringsById}
              currency={currency}
              quotePrices={quotePrices}
              loading={loading}
            />
          </MAccordionItem>
        </MAccordion>
      );
    },
  );

export { QuoteFormPricesSection };
